import type { AuthTokens, LoginOutcome } from './auth.models';

/**
 * Parsing for the `rem-backend` standard response envelope.
 * See `src/app/features/auth/FRONTEND-API-CHANGES.md` (§0 envelope, §1 auth).
 * Payloads always live under `data`; there are no legacy snake_case / `twofector` shims.
 */

/**
 * Unwrap the standard envelope's `data` object. Tolerates already-unwrapped bodies
 * (e.g. test fixtures or a `data`-less response) by returning the record as-is.
 */
export function unwrapEnvelopeData(body: unknown): Record<string, unknown> | null {
  if (!body || typeof body !== 'object' || Array.isArray(body)) {
    return null;
  }
  const record = body as Record<string, unknown>;
  const data = record['data'];
  if (data && typeof data === 'object' && !Array.isArray(data)) {
    return data as Record<string, unknown>;
  }
  return record;
}

/** Stable machine error code (`errorCode`) from an error envelope, upper-cased; `null` if absent. */
export function readApiErrorCode(body: unknown): string | null {
  if (!body || typeof body !== 'object') {
    return null;
  }
  const code = (body as Record<string, unknown>)['errorCode'];
  return typeof code === 'string' && code ? code.toUpperCase() : null;
}

function readTokenString(value: unknown): string | null {
  if (typeof value !== 'string') {
    return null;
  }
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
}

/**
 * Reads the token pair from the envelope. `POST /auth/login` nests them under `data.tokens`;
 * `POST /auth/refresh` returns them flat under `data`. Both are handled here.
 */
export function extractTokens(body: unknown): AuthTokens | null {
  const data = unwrapEnvelopeData(body);
  if (!data) {
    return null;
  }
  const nested = data['tokens'];
  const source =
    nested && typeof nested === 'object' && !Array.isArray(nested)
      ? (nested as Record<string, unknown>)
      : data;
  const accessToken = readTokenString(source['accessToken']);
  if (!accessToken) {
    return null;
  }
  return {
    accessToken,
    refreshToken: readTokenString(source['refreshToken']) ?? undefined,
    expiresIn: readTokenString(source['expiresIn']) ?? undefined,
  };
}

/**
 * The MFA step is signalled by `data.mfaRequired === true` on a `200` login response
 * (§1 "Response A — MFA required"). Returns the challenge details, or `null` when no MFA is needed.
 */
export function resolveMfaChallenge(body: unknown): LoginOutcome | null {
  const data = unwrapEnvelopeData(body);
  if (!data || data['mfaRequired'] !== true) {
    return null;
  }
  const authType =
    typeof data['authenticationType'] === 'string' ? data['authenticationType'] : undefined;
  const email = typeof data['email'] === 'string' ? data['email'] : undefined;
  return { outcome: 'mfa_required', authenticationType: authType, emailHint: email };
}
